import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@shared/services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { formatEpoch } from '../sync-monitoring-format';
import { ISourceDriftLeaf, ISourceDriftRun, SourceDriftStartResult } from '../sync-monitoring.models';
import { SyncMonitoringService } from '../sync-monitoring.service';
import { sourceDriftKey } from '../sync-monitoring.utils';

const NS = 'administration.sync-monitoring.source-drift';
const EXPIRY_REFUSED = new Set(['overProportion', 'overRunCap']);

export interface ISourceDriftDialogData {
	environmentId: string;
	tableName: string;
	/** Whether the caller may start a range check (one:ManualCleanup). */
	canManage: boolean;
}

/** A table's latest source drift range-check run with its leaves, and the action that starts a new check. The dialog loads on open and doesn't read the table row, which a refresh rebuilds. */
@Component({
	selector: 'app-source-drift-dialog',
	templateUrl: './source-drift-dialog.component.html',
	styleUrls: ['../audit-dialogs.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class SourceDriftDialogComponent implements OnInit, OnDestroy {
	/** Emitted when a range check was started, so the page reloads its environment data. */
	@Output() readonly started = new EventEmitter<void>();

	public isLoading = true;
	public isStarting = false;
	public run: ISourceDriftRun | null = null;
	/** The translation key of why the run is not shown. */
	public errorKey: string | null = null;
	/** The translation key of why the last start failed. Shown in the dialog, not as a notification. */
	public startErrorKey: string | null = null;
	public missingTotal = 0;
	public extraTotal = 0;

	readonly formatEpoch = formatEpoch;
	readonly sourceDriftKey = sourceDriftKey;

	private destroy$ = new Subject<void>();

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ISourceDriftDialogData,
		private syncMonitoringService: SyncMonitoringService,
		private notificationService: NotificationService,
		private dialogRef: MatDialogRef<SourceDriftDialogComponent>,
	) {}

	ngOnInit(): void {
		this.load();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get canStart(): boolean {
		return this.data.canManage && !this.isStarting && !this.isLoading && this.run?.state !== 'running';
	}

	/** The `expiryRefused` text key; a value without its own text gets the generic one, which shows it raw. */
	expiryRefusedKey(leaf: ISourceDriftLeaf): string {
		return `${NS}.dialog.expiry-refused.${leaf.expiryRefused && EXPIRY_REFUSED.has(leaf.expiryRefused) ? leaf.expiryRefused : 'unknown'}`;
	}

	copy(keys: string[]): void {
		navigator.clipboard?.writeText(keys.join('\n')).then(
			() => this.notificationService.notify([`${NS}.dialog.copied`], [], 'success'),
			() => this.notificationService.notify([`${NS}.dialog.copy-failed`], [], 'error'),
		);
	}

	startCheck(): void {
		if (!this.canStart) {
			return;
		}
		this.isStarting = true;
		this.startErrorKey = null;
		// Closing now would drop the result of a check core-one may already have accepted.
		this.dialogRef.disableClose = true;
		this.syncMonitoringService
			.startSourceDriftCheck(this.data.environmentId, this.data.tableName)
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.isStarting = false;
				this.dialogRef.disableClose = false;
				this.showStartResult(result);
				if (result.kind === 'started') {
					this.started.emit();
				}
				if (result.kind === 'started' || result.kind === 'conflict') {
					this.load();
				}
			});
	}

	private showStartResult(result: SourceDriftStartResult): void {
		const start = `${NS}.dialog.start`;
		switch (result.kind) {
			case 'started':
				return this.notificationService.notify([`${start}.started`], [], 'success');
			case 'conflict':
				this.startErrorKey = `${start}.conflict`;
				return;
			case 'rejected':
				this.startErrorKey = `${start}.rejected`;
				return;
			case 'forbidden':
				this.startErrorKey = `${start}.forbidden`;
				return;
			case 'notAvailable':
				this.startErrorKey = `${start}.not-available`;
				return;
			default:
				this.startErrorKey = `${start}.failed`;
		}
	}

	private load(): void {
		this.isLoading = true;
		this.syncMonitoringService
			.getSourceDriftCheck(this.data.environmentId, this.data.tableName)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (run) => {
					this.run = run;
					this.errorKey = null;
					this.missingTotal = (run.leaves ?? []).reduce((sum, leaf) => sum + (leaf.missing ?? 0), 0);
					this.extraTotal = (run.leaves ?? []).reduce((sum, leaf) => sum + (leaf.extra ?? 0), 0);
					this.isLoading = false;
				},
				error: (error: unknown) => {
					this.run = null;
					this.errorKey = error instanceof HttpErrorResponse && error.status === 404 ? `${NS}.dialog.no-run` : `${NS}.dialog.load-failed`;
					this.isLoading = false;
				},
			});
	}
}
